<template>
	<div class="row px-6 pb-8">
		<div class="col-12 centered variations-list-container">
			<AppLoader v-if="loadingItems" />
			<AppSortableList ref="variations-items-list" @sorting="startSorting" @sorted="changeListsOrder" v-if="!loadingItems && loadedItems && listProdsArray.length" full-width :hasAddItem="false" :items="listProdsArray" key-prop="tempid">
				<template v-slot:summary-prods-heading="{item}">
					<div class="row summary-prod-row heading-row">
						<img class="handle icon-dimension-20 no-pointer-events" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" draggable="false" style="visibility: hidden">
						<p class="prod-title">{{ getString('product_name') }}</p>
						<span class="prod-price">{{ getString('price_symbol', [currencyStore.currCurrency?.symbol]) }}</span>
						<div class="list-icons-wrapper"></div>
						<AppButtonIcon :small="windowWidth < 560" class="prod-row-action" style="visibility: hidden" icon-name="icon-edit-white" />
					</div>
				</template>
				<template v-slot:summary-prod="{item}">
					<div class="row summary-prod-row" :class="{'meta-product': item.meta_product}">
						<img class="handle icon-dimension-20" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" :title="getString('drag_to_sort')"  draggable="false">
						<p @click="clickVariation(item.tempid)" class="prod-title" :class="{nested: !!item.nested}">{{ item.title }}</p>
						<span @click="clickVariation(item.tempid)" class="prod-price">{{ currencyStore.readableCurrencyValue(item.product_price, true, false) }}</span>
						<div @click="clickVariation(item.tempid)" class="list-icons-wrapper" :class="{nested: !!item.nested}">
							<div class="list-icon" v-show="!item.meta_product" :title="getString(item.linked_name ? 'linked_name' : 'unlinked_name')" :class="{active: !item.linked_name}" v-html="getIcon('iconNameLinked')"></div>
							<div class="list-icon" v-show="!item.meta_product" :title="getString(item.linked_price ? 'linked_price' : 'unlinked_price')" :class="{active: !item.linked_price}" v-html="getIcon('iconPriceLinked')"></div>

							<div class="list-icon availability-icon" v-show="!!item.meta_product" :title="getString(!!item.available ? 'available' : 'unavailable')" :class="{active: !!item.available}" v-html="getIcon(!!item.available ? 'iconEye' : 'iconEyeSlash')"></div>
							<div class="list-icon active nested" v-show="!!item.nested" :title="getString('nested')" v-html="getIcon('iconNested')"></div>
						</div>
						<AppButtonIcon @click="clickVariation(item.tempid)" :ref="'button-icon-'+item.tempid" :active="item.active && !item.deleted_product" :title="getString('edit')" :small="windowWidth < 560" class="prod-row-action" icon-name="icon-edit-white" />
					</div>
				</template>
			</AppSortableList>
			<div v-if="!listProdsArray.length" class="row centered p-10 ">
				<p class="col-12 centered mb-10">{{ getString('empty_list') }}</p>
				<AppButton small outline @click="clickAddNewProduct" :text="getString('add_product')" />
			</div>
		</div>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="!!activeSheetListItemTempId" @toggling-popup="togglingBottomSheet">
			<div v-if="!!activeSheetListItemTempId">
				<div class="row centered">
					<AppLoader v-if="loadingItems" />
				</div>
				<ListItemEditor @delete-product="deleteCurrentEditingListItem" @confirm-list-item="confirmEditListItem" :list-item="selectedListItem" />
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppSortableList from "@/shared/components/AppSortableList.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ListItemEditor from "@/console-new/components/config/menu/ListItemEditor.vue";
import {useUserStore} from "@/console-new/stores";

import {useCurrencyStore, useLanguageStore, useSvgStore} from "@/shared/stores";
import {useMenuStore} from "@/console-new/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let svgStore = useSvgStore();
		let userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, getIcon: svgStore.get, menuStore, currencyStore, userStore};
	},
	emits: ['add-new-product'],
	mixins: [WindowWidth],
	name: "CompositeEditorSummary",
	components: {AppCard, AppButtonIcon, AppSortableList, ListItemEditor, AppBottomSheet, AppClickableIcon, AppLoader, AppButton},
	props: {
		categoryTempId: {
			type: String,
			required: true
		},
		itemTempId: {
			type: String,
			required: true
		},
		listTempId: {
			type: String,
			required: true
		},
	},
	data() {
		return {
			loadingItems: false,
			loadedItems: false,
			sorting: false,
			activeSheetListItemTempId: null,
		}
	},
	computed: {
		menuItem() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		list() {
			return this.menuItem.lists[this.listTempId];
		},
		products() {
			return this.list.list_products;
		},
		listProdsArray() {
			let returning = [];
			if(this.products) {
				Object.entries(this.products).forEach(([key, prod]) => {
					if(!prod.deleted_product) {
						returning.push(prod);
					}
				});
			}
			let firstItem = {
				title: 'name',
				customSlot: 'summary-prods-heading',
				noSort: true
			}
			if(returning?.length) {
				returning = [firstItem, ...returning];
			}
			return returning;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		selectedListItem() {
			if(!this.activeSheetListItemTempId || !this.products || !this.products[this.activeSheetListItemTempId]) {
				return null;
			}
			return this.products[this.activeSheetListItemTempId];
		}
	},
	methods: {
		deleteCurrentEditingListItem() {
			this.selectedListItem.deleted_product = true;
			this.selectedListItem.active = false;
			this.activeSheetListItemTempId = null;
		},
		confirmEditListItem(emittedItem) {

			this.selectedListItem.title = emittedItem.productName;
			this.selectedListItem.product_name = emittedItem.productName;
			this.selectedListItem.product_price = emittedItem.productPrice;
			if(!this.selectedListItem.meta_product) {
				this.selectedListItem.linked_name = emittedItem.linkedName;
				this.selectedListItem.linked_price = emittedItem.linkedPrice;
			} else {
				this.selectedListItem.available = !!emittedItem.available;
			}

			this.activeSheetListItemTempId = null;
		},
		togglingBottomSheet(isOpen) {
			if(!isOpen) {
				this.activeSheetListItemTempId = null;
				this.listProdsArray.forEach((prod, index) => {
					//First item is the heading
					if(index !== 0) {
						this.$refs['button-icon-'+prod.tempid].isActive = false;
					}
				});
			}
		},
		startSorting() {
			this.sorting = true;
		},
		clickVariation(tempId) {
			if(this.sorting) { return; }
			this.listProdsArray.forEach(prod => {
				if(this.$refs['button-icon-'+prod.tempid]) {
					this.$refs['button-icon-'+prod.tempid].isActive = prod.tempid === tempId ? !prod.active : false;
				}
			});
			this.activeSheetListItemTempId = tempId;
		},
		changeListsOrder(evt, newOrder) {
			for(let pos = 0; pos < newOrder.length; pos++) {
				if(newOrder[pos]) {
					let listProdTempId = newOrder[pos].tempid;
					this.products[listProdTempId].order_number = pos;
				}
			}
			this.sorting = false;
		},
		fetchSummaryItems() {
			this.loadingItems = true;
			this.loadedItems = false;
			if(this.products) {
				this.loadingItems = false;
				this.loadedItems = true;
			} else {
				if(this.menuItem.menu_item_id) {
					this.loadedItems = false;
					this.menuStore.getListInformation(this.menuItem.menu_item_id, this.listTempId, () => {
						this.loadingItems = false;
						this.loadedItems = true;
					});
				}
			}
		},
		clickAddNewProduct() {
			this.$emit('add-new-product');
		}
	},
	mounted() {
		this.fetchSummaryItems();
	}


}
</script>

<style scoped lang="scss">
.icon-dimension-20 {
	width: 20px;
	height: 20px;
}
.summary-prod-row {
	@include centered;
	justify-content: space-between;
	padding: 0 10px;
	cursor: pointer;

	@media (max-width: 400px) {
		padding: 0 6px;
	}

	&.heading-row {
		background-color: var(--color-bg-primary-2);
		pointer-events: none;

		.prod-title, .prod-price {
			color: var(--txt-2);
			font-size: 10px;
			white-space: nowrap;
		}

	}
}
.cover-container {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	box-shadow: inset 0 0 3px var(--color-box-shadow-3);
	background-color: var(--color-alpha-1);
	border-radius: 7px;
	padding: 4px 6px;
	margin-left: 4px;
	gap: 5px;

}
.prod-title {
	@include truncateString;
	width: calc(100% - 230px);

	@media (max-width: 560px) {
		width: calc(100% - 178px);
		font-size: 14px;
	}
	@media (max-width: 400px) {
		width: calc(100% - 155px);
		font-size: 12px;
	}
	@media (max-width: 320px) {
		width: calc(100% - 154px);
	}
}
.prod-price {
	width: 80px;
	text-align: right;

	@media (max-width: 560px) {
		padding-right: 2px;
	}

	@media (max-width: 400px) {
		width: 60px;
		font-size: 12px;
	}
}
.list-icons-wrapper {
	display: flex;
	align-items: center;
	justify-content: space-evenly;
	height: 25px;
	min-height: 25px;
	width: 60px;

	box-shadow: inset 0 0 3px var(--color-box-shadow-3);
	background-color: var(--color-alpha-1);
	border-radius: 7px;
	padding: 4px 0;

	@media (max-width: 560px) {
		width: 54px;
	}
	@media (max-width: 400px) {
		width: 46px;
	}

	.list-icon {
		width: 16px;
		height: 20px;
		&.nested {
			height: 16px;
		}
		@media (max-width: 560px) {
			width: 10px;
		}
		&.availability-icon {
			width: 16px;
			height: 20px;
			@media (max-width: 560px) {
				width: 10px;
			}
		}
		:deep(svg) {
			width: 100%;
			height: 100%;
			max-width: 100%;
			max-height: 100%;
			.fill { fill: var(--color-txt-2);	}
			.stroke { stroke: var(--color-txt-2);	}
		}

		&.active {
			:deep(svg) {
				.fill { fill: var(--color-primary);	}
				.stroke { stroke: var(--color-primary);	}
			}
		}
	}

}
.variations-list-container {
	:deep(.list-item) {
		padding: 0;
		min-height: unset;
		overflow: hidden;
	}
}
.prod-row-action {
	margin: 0;
}
.hide-button {
	visibility: hidden;
}
@media (max-width: 560px) {
	.sortable-list-container .list-item .handle {
		margin-right: 2px;
		width: 16px;
	}
}

</style>